import { colorNameTitle } from 'constants/global';
import { ColorValuesByThemeType, PaletteValuesByThemeType } from 'modules/settingsContainer/ColorPicker/types';
import { DefaultDataSettings } from 'modules/settingsContainer/common/data/DefaultDataSettings';
import { FormattingSettings } from 'modules/settingsContainer/common/data/FormattingSettings';
import { LimitSettings } from 'modules/settingsContainer/common/data/LimitSettings';
import { LineAndBarShowValueSettings } from 'modules/settingsContainer/common/data/LineAndBarShowValueSettings';
import { MetricSettings } from 'modules/settingsContainer/common/data/MetricSettings';
import { FieldSettingsRenderType, MetricsRenderType } from 'modules/settingsContainer/common/data/MetricSettings/types';
import { MinAndMaxSettings } from 'modules/settingsContainer/common/data/MinAndMaxSettings';
import { NameFromDatabaseSettings } from 'modules/settingsContainer/common/data/NameFromDatabaseSettings';
import { SqlSettings } from 'modules/settingsContainer/common/data/SqlSettings';
import { FictionalMetricField } from 'modules/settingsContainer/common/FictionalMetricField';
import { GradientColorBySettings } from 'modules/settingsContainer/common/GradientColorBySettings';
import { IndicatorMetricField } from 'modules/settingsContainer/common/IndicatorMetricField';
import { ModelSelectorSettings } from 'modules/settingsContainer/common/ModelSelectorSettings';
import { SettingsFieldEntry } from 'modules/settingsContainer/SettingsFieldEntry';
import {
  onAddNewIncision,
  onAddNewIndicator,
  onChangeEmptyValue,
  onChangeFictionalData,
  onChangeIncisionCustomRequest,
  onChangeIncisionFieldName,
  onChangeIncisionName,
  onChangeIncisionNameFromDataBase,
  onChangeIndicatorCustomRequest,
  onChangeIndicatorName,
  onDeleteIncision,
  onDeleteIndicator,
  onLimitChange,
  onModelIdChange,
  onSqlSettingsSave,
} from 'modules/visualisations/common/onChangeFunctions';
import {
  onAddNewVerticalIncision,
  onChangeEmptyValueVerticalIncision,
  onChangeFictionalDataVerticalIncision,
  onChangeIndicatorFormatting,
  onChangeIndicatorNameFromDataBase,
  onChangeShowValue,
  onChangeVerticalIncisionCustomRequest,
  onChangeVerticalIncisionFieldName,
  onChangeVerticalIncisionName,
  onChangeVerticalIncisionNameFromDataBase,
  onColorIncisionChange,
  onColorIndicatorChange,
  onColorVerticalIncisionChange,
  onDeleteVerticalIncision,
  onHeatmapBackgroundBySettingsChange,
  onMinAndMaxChange,
  onMoveIncisionHeatmap,
} from 'modules/visualisations/Heatmap/settings/DataTab/constants';
import { useDataSettingsMetric } from 'modules/visualisations/hooks/dataSettingsMetric';
import React from 'react';
import {
  defaultHeatmapDataSettings,
  defaultHeatmapViewSettings,
  defaultSqlDataSettings,
  getVisualisationFieldName,
} from 'store/reducers/visualisations/constants';
import { HeatmapIncisionInterface, HeatmapIndicatorInterface } from 'store/reducers/visualisations/types';
import { useGetActiveVisualisationSettings } from 'utils/hooks/visualisation/getActiveVisualisationSettings';
import { getIncisionAndIndicatorsCompletions } from 'utils/sqlSettings';
import { KeysIncisionsHeatmapEnum } from '../../visualisation/types';
import { EmptyValuesElementSettings } from 'modules/settingsContainer/common/data/EmptyValuesElementSettings';

export const DataTab = () => {
  const { dataSettings, codeEditorData, astOfVisualisation } = useGetActiveVisualisationSettings({
    defaultData: defaultHeatmapDataSettings,
    defaultView: defaultHeatmapViewSettings,
    defaultSqlData: defaultSqlDataSettings,
  });

  const indicators = dataSettings.indicators;

  const { isRealData } = dataSettings,
    { tableFields, controls, onMetricClick, onSetupClick, setupIsOpen, metricIsSelected, modelMetaData } = useDataSettingsMetric(
      dataSettings.modelId,
    );

  const incisionRender: MetricsRenderType<HeatmapIncisionInterface> = ({ metrics }) => (
    <>
      {metrics.map(({ fieldName, name, fictionalData, id, settings: { nameFromDatabase, customRequest } }) => {
        const onUpClick = () => onMoveIncisionHeatmap(dataSettings, id, 'up', KeysIncisionsHeatmapEnum.INCISIONS);
        const onDownClick = () => onMoveIncisionHeatmap(dataSettings, id, 'down', KeysIncisionsHeatmapEnum.INCISIONS);
        const onChangeColors = (colors: PaletteValuesByThemeType | null) => {
          onColorIncisionChange(dataSettings, colors, id);
        };

        return (
          <SettingsFieldEntry
            setupIsOpen={setupIsOpen(id)}
            onSetupClick={onSetupClick(id)}
            onClick={onMetricClick(id)}
            isSelected={metricIsSelected(id)}
            fieldValue={getVisualisationFieldName({ nameFromDatabase, fieldName, name })}
            canChangeField={!nameFromDatabase}
            onFieldChange={(name) => onChangeIncisionName(dataSettings, name, id)}
            onChangeColors={onChangeColors}
            onUpClick={onUpClick}
            onDownClick={onDownClick}
            colorPickerType="palette"
            disableColorPicker
            key={id}
          >
            <FictionalMetricField
              onCustomRequestChange={(customRequest) => onChangeIncisionCustomRequest(dataSettings, customRequest, id)}
              customRequest={customRequest || ''}
              disabled={!!customRequest}
              isRealData={isRealData}
              options={tableFields}
              value={{ fictionalData, fieldName }}
              onChange={({ fictionalData, fieldName }) => {
                fieldName && onChangeIncisionFieldName(dataSettings, fieldName || '', id);
                fictionalData && onChangeFictionalData(dataSettings, fictionalData, id);
              }}
              modelMetaData={modelMetaData}
            />
          </SettingsFieldEntry>
        );
      })}
    </>
  );

  const verticalIncisionRender: MetricsRenderType<HeatmapIncisionInterface> = ({ metrics }) => (
    <>
      {metrics.map(({ fieldName, name, fictionalData, id, settings: { nameFromDatabase, customRequest } }) => {
        const onUpClick = () => onMoveIncisionHeatmap(dataSettings, id, 'up', KeysIncisionsHeatmapEnum.VERTICAL_INCISIONS);
        const onDownClick = () => onMoveIncisionHeatmap(dataSettings, id, 'down', KeysIncisionsHeatmapEnum.VERTICAL_INCISIONS);
        const onChangeColors = (colors: PaletteValuesByThemeType | null) => {
          onColorVerticalIncisionChange(dataSettings, colors, id);
        };

        return (
          <SettingsFieldEntry
            setupIsOpen={setupIsOpen(id)}
            onSetupClick={onSetupClick(id)}
            onClick={onMetricClick(id)}
            isSelected={metricIsSelected(id)}
            fieldValue={getVisualisationFieldName({ nameFromDatabase, fieldName, name })}
            canChangeField={!nameFromDatabase}
            onFieldChange={(name) => onChangeVerticalIncisionName(dataSettings, name, id)}
            onChangeColors={onChangeColors}
            onUpClick={onUpClick}
            onDownClick={onDownClick}
            colorPickerType="palette"
            disableColorPicker
            key={id}
          >
            <FictionalMetricField
              onCustomRequestChange={(customRequest) => onChangeVerticalIncisionCustomRequest(dataSettings, customRequest, id)}
              customRequest={customRequest || ''}
              disabled={!!customRequest}
              isRealData={isRealData}
              options={tableFields}
              value={{ fictionalData, fieldName }}
              onChange={({ fictionalData, fieldName }) => {
                fieldName && onChangeVerticalIncisionFieldName(dataSettings, fieldName || '', id);
                fictionalData && onChangeFictionalDataVerticalIncision(dataSettings, fictionalData, id);
              }}
              modelMetaData={modelMetaData}
            />
          </SettingsFieldEntry>
        );
      })}
    </>
  );

  const verticalIncisionFieldSettingsRender: FieldSettingsRenderType<HeatmapIncisionInterface> = ({ metric: incision }) => {
    const {
      id,
      emptyValues: { isEmptyValue, value },
      settings: { nameFromDatabase },
    } = incision;

    return (
      <>
        <EmptyValuesElementSettings
          switcherValue={isEmptyValue}
          value={value}
          onChange={(value) => onChangeEmptyValueVerticalIncision(dataSettings, value, id)}
        />
        <NameFromDatabaseSettings
          value={nameFromDatabase}
          onChange={(nameFromDatabase: boolean) => onChangeVerticalIncisionNameFromDataBase(dataSettings, nameFromDatabase, id)}
        />
      </>
    );
  };

  const incisionFieldSettingsRender: FieldSettingsRenderType<HeatmapIncisionInterface> = ({ metric: incision }) => {
    const {
      id,
      settings: {
        emptyValues: { isEmptyValue, value },
        nameFromDatabase,
      },
    } = incision;

    return (
      <>
        <EmptyValuesElementSettings
          switcherValue={isEmptyValue}
          value={value}
          onChange={(value) => onChangeEmptyValue(dataSettings, value, id)}
        />
        <NameFromDatabaseSettings
          value={nameFromDatabase}
          onChange={(nameFromDatabase: boolean) => onChangeIncisionNameFromDataBase(dataSettings, nameFromDatabase, id)}
        />
      </>
    );
  };

  const indicatorRender: MetricsRenderType<HeatmapIndicatorInterface> = ({ metrics }) => (
    <>
      {metrics.map(({ id, fieldName, name, operationType, customRequest, settings: { nameFromDatabase } }) => {
        const onChangeColors = (colors: ColorValuesByThemeType | null) => onColorIndicatorChange(dataSettings, colors, id);

        return (
          <SettingsFieldEntry
            setupIsOpen={setupIsOpen(id)}
            onSetupClick={onSetupClick(id)}
            onClick={onMetricClick(id)}
            isSelected={metricIsSelected(id)}
            fieldValue={getVisualisationFieldName({ name, nameFromDatabase, fieldName })}
            canChangeField={!nameFromDatabase}
            onFieldChange={(name) => onChangeIndicatorName(dataSettings, name, id)}
            onChangeColors={onChangeColors}
            disableColorPicker
            key={id}
            disableChangePriory
          >
            <IndicatorMetricField
              onCustomRequestChange={(customRequest) => onChangeIndicatorCustomRequest(dataSettings, customRequest, id)}
              customRequest={customRequest}
              options={tableFields}
              dataSettings={dataSettings}
              id={id}
              isRealData={isRealData}
              fieldName={fieldName}
              operationType={operationType}
              modelMetaData={modelMetaData}
            />
          </SettingsFieldEntry>
        );
      })}
    </>
  );

  const indicatorFieldSettingsRender: FieldSettingsRenderType<HeatmapIndicatorInterface> = ({ metric: indicator }) => {
    const {
      id,
      settings: { nameFromDatabase, showValue, formatting },
    } = indicator;

    return (
      <>
        <NameFromDatabaseSettings
          value={nameFromDatabase}
          onChange={(nameFromDatabase: boolean) => onChangeIndicatorNameFromDataBase(dataSettings, nameFromDatabase, id)}
        />
        <LineAndBarShowValueSettings
          type="bar"
          indicators={indicators.filter((indicator) => indicator.id === id)}
          value={showValue}
          onChange={(showValue) => onChangeShowValue(dataSettings, showValue, id)}
        />
        <FormattingSettings value={formatting} onChange={(value) => onChangeIndicatorFormatting(dataSettings, value, id)} />
      </>
    );
  };

  return (
    <>
      <DefaultDataSettings dataSettings={dataSettings} />
      <ModelSelectorSettings value={dataSettings.modelId} onChange={onModelIdChange} />
      <MetricSettings
        titleText="Разрезы по X"
        addButtonText="Добавить разрез"
        fieldSettingsRender={incisionFieldSettingsRender}
        metricRender={incisionRender}
        metrics={dataSettings.incisions}
        onAdd={() => onAddNewIncision(dataSettings, 'heatmap')}
        onDelete={(id) => id && onDeleteIncision(dataSettings, id)}
        controls={controls}
      />
      <MetricSettings
        titleText="Разрезы по Y"
        addButtonText="Добавить разрез"
        fieldSettingsRender={verticalIncisionFieldSettingsRender}
        metricRender={verticalIncisionRender}
        metrics={dataSettings.verticalIncisions}
        onAdd={() => onAddNewVerticalIncision(dataSettings)}
        onDelete={(id) => id && onDeleteVerticalIncision(dataSettings, id)}
        controls={controls}
      />
      <MetricSettings
        titleText="Показатели"
        fieldSettingsRender={indicatorFieldSettingsRender}
        metricRender={indicatorRender}
        metrics={dataSettings.indicators}
        onAdd={() => onAddNewIndicator(dataSettings, 'heatmap')}
        onDelete={(id) => id && onDeleteIndicator(dataSettings, id)}
        controls={controls}
        disableAddingMetric
      />
      <SqlSettings
        //@ts-ignore
        astData={astOfVisualisation}
        sqlData={codeEditorData}
        adviceEditorIncision={getIncisionAndIndicatorsCompletions(dataSettings.incisions)}
        adviceEditorIndicator={getIncisionAndIndicatorsCompletions(indicators)}
        onSave={(sqlSettingsChanges) =>
          onSqlSettingsSave(dataSettings, sqlSettingsChanges, 'heatmap', {
            indicator: { minValue: 1, maxValue: 1 },
            incision: { minValue: 2 },
          })
        }
        modelMetaData={modelMetaData}
      />
      <GradientColorBySettings
        disabledComponentByCondition
        title={colorNameTitle}
        indicators={dataSettings.indicators}
        value={dataSettings.gradientBackgroundByValueSettings}
        onChange={onHeatmapBackgroundBySettingsChange}
      />
      <LimitSettings value={dataSettings.limit} onChange={onLimitChange} />
      <MinAndMaxSettings title="Мин/Макс для основных" value={dataSettings.minAndMax} onChange={onMinAndMaxChange} />
    </>
  );
};
